import {AfterViewInit, Component, OnInit, ViewChild, ViewEncapsulation, inject} from "@angular/core";
import {FullCalendarComponent, FullCalendarModule} from "@fullcalendar/angular";
import {DateTime} from "luxon";
import {debounceTime, distinctUntilChanged, Subject, takeUntil} from "rxjs";
import {CalendarOptions, DatesSetArg, EventContentArg} from "@fullcalendar/core";
import {TranslatePipe} from "@ngx-translate/core";
import {ActivatedRoute, Router, RouterLink} from "@angular/router";
import {ParseIsoPipeModule} from "ngx-date-fns";
import {ToastrService} from "ngx-toastr";
import {EventCalendarInfoboxComponent, InfoboxData, InfoboxLine} from "./infobox/event-calendar-infobox.component";
import {TableFilterComponent} from '../../components/table-filter/table-filter.component';
import {TableFilterLoaderComponent} from '../../components/table/table-filter-loader/table-filter-loader.component';
import {FormatInTimeZonePipe} from '../../pipes/format-in-time-zone/format-in-time-zone.pipe';
import AuthenticatedBaseComponent from '../authenticated-base.component';
import {EventCalendarToolbarComponent} from './event-calendar-toolbar/event-calendar-toolbar.component';
import {TableDisplayConfig} from '../../entities/table-display-config.entity';
import {EventCalendarConfigService, CalendarView} from './event-calendar-config.service';
import {EventCalendarDataService} from './event-calendar-data.service';
import {IfHasAllPermissionsDirective} from '../../directives/if-has-all-permissions.directive';
import {StickyOccurrencesDirective} from '../../directives/sticky-occurrences.directive';
import {PERMISSION, ServiceProviderHub} from '@generated-backend-types';
import TableConfig = ServiceProviderHub.App.Http.Resources.ServiceProviderHub.Meta.TableConfig;
import EventCalendarEntry = ServiceProviderHub.App.Http.Resources.ServiceProviderHub.Eventmanagement.Calendar.EventCalendarEntry;
import {areObjectsIdentical} from '../../utils/are-objects-identical';
import EventCalendarMonthEntry = ServiceProviderHub.App.Http.Resources.ServiceProviderHub.Eventmanagement.Calendar.Month.EventCalendarMonthEntry;
import Occurrence = ServiceProviderHub.App.Http.Resources.ServiceProviderHub.Eventmanagement.Occurrence.Occurrence;
import EventPhaseEnum = ServiceProviderHub.App.Enums.Eventmanagement.EventPhaseEnum;
import EventmanagementCalendarEntryTypeEnum = ServiceProviderHub.App.Enums.Eventmanagement.Calendar.EventmanagementCalendarEntryTypeEnum;
import OccurrenceCategoryEnum = ServiceProviderHub.App.Enums.Eventmanagement.Occurrence.OccurrenceCategoryEnum;
import {NgIf} from '@angular/common';
import {BERLIN_TZ, toBerlinIso} from './event-calendar.constants';

enum CalendarViewEnum {
  dayGridMonth = 'dayGridMonth',
  timeGridWeek = 'timeGridWeek',
  dayGridWeek = 'dayGridWeek',
  timeGridDay = 'timeGridDay',
  dayGridDay = 'dayGridDay',
}

@Component({
  selector: 'app-event-calendar',
  templateUrl: './event-calendar.component.html',
  styleUrls: ['./event-calendar.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    FullCalendarModule,
    TableFilterComponent,
    TableFilterLoaderComponent,
    RouterLink,
    FormatInTimeZonePipe,
    ParseIsoPipeModule,
    EventCalendarToolbarComponent,
    IfHasAllPermissionsDirective,
    StickyOccurrencesDirective,
    TranslatePipe,
    NgIf,
  ]
})
export class EventCalendarComponent extends AuthenticatedBaseComponent implements OnInit, AfterViewInit {
  private readonly toastrService = inject(ToastrService);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);

  @ViewChild('calendarComponent') public calendarComponent?: FullCalendarComponent;
  @ViewChild(StickyOccurrencesDirective) private stickyOccurrences?: StickyOccurrencesDirective;

  private readonly configService = inject(EventCalendarConfigService);
  private readonly dataService = inject(EventCalendarDataService);

  /** Calendar properties */
  public currentView: CalendarViewEnum = CalendarViewEnum.timeGridWeek;
  public view: 'day' | 'week' | 'month' = 'day';

  /** Date properties */
  public dateValid = true;
  public currentStart?: DateTime;

  /** fire whenever you want to change the date */
  public changeDate = new Subject<{
    start: DateTime,
    updateHistory: boolean,
    view: string,
    filter: Record<string, string>,
    favorite?: string
  }>();

  /** fires when the report timespan was changed and should be loaded */
  public dateChanged = new Subject<{
    start: DateTime,
    view: CalendarViewEnum
    filter: Record<string, string>
    favorite: string | undefined
  }>();

  /** Filter & Favorite properties */
  public showFilters = false;
  public filter: Record<string, string> = {};
  public favorite?: string;

  public calendarOptions: CalendarOptions = {
    ...this.configService.buildBaseOptions(this.locale),
    customButtons: this.buildCustomButtons(),
    resourceAreaHeaderContent: () => this.view !== 'month' ? `KW ${this.currentStart?.toFormat('WW')}` : '',
    datesSet: (dates: DatesSetArg) => this.emitChangeDate(DateTime.fromJSDate(dates.start).setZone(BERLIN_TZ)),
    eventsSet: () => {
      this.stickyOccurrences?.refresh();
    },
  };

  public amountOfFilters = 0;
  public tableDisplayConfig: TableDisplayConfig = {
    column_order: '',
    favorite: undefined,
    filter: {},
    hidden_columns: '',
    page: 0,
    column_widths: {},
    per_page: 30,
    sorts: '-updated_at',
    tableConfig: {} as TableConfig
  };
  protected readonly EventPhaseEnum = EventPhaseEnum;
  protected readonly CalendarViewEnum = CalendarViewEnum;
  protected readonly EventmanagementCalendarEntryTypeEnum = EventmanagementCalendarEntryTypeEnum;
  protected readonly OccurrenceCategoryEnum = OccurrenceCategoryEnum;
  protected readonly Date = Date;
  protected readonly DateTime = DateTime;

  public ngOnInit() {
    // No takeUntil — this *is* the destroy notification.
    this.onDestroy$.subscribe(() => this.toastrService.clear());

    this.currentStart = DateTime.fromJSDate(new Date()).setZone(BERLIN_TZ).startOf('day');

    this.changeDate.pipe(
      debounceTime(100),
      distinctUntilChanged(),
      takeUntil(this.onDestroy$)
    ).subscribe(value => {
        this.currentStart = value.start;
        this.router.navigate([], {
          queryParams: {start: value.start.toISO({suppressMilliseconds: true})},
          replaceUrl: !value.updateHistory,
        }).then(() => this.loadData());
      });

    this.dateChanged.pipe(takeUntil(this.onDestroy$)).subscribe(date => {
      this.currentStart = date.start;
      this.calendarComponent?.getApi().gotoDate(date.start.toISO() as string);
      this.calendarOptions.initialDate = date.start.toISO() as string;
    });

    this.activatedRoute.queryParams.pipe(takeUntil(this.onDestroy$)).subscribe(params => {
      if (params['start']) {
        this.dateChanged.next({
          start: DateTime.fromISO(params['start']).setZone(BERLIN_TZ, {keepLocalTime: false}),
          view: this.currentView,
          filter: this.filter,
          favorite: this.favorite,
        });
      }
    });

    this.localeService.localeChanged.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.loadData();

      const labels = this.configService.buttonLabels(this.locale);
      for (const view of (['day', 'week', 'month'] as const)) {
        const button = this.calendarOptions.customButtons?.[view];
        if (button) button.text = labels[view];
      }
      this.calendarOptions.locale = this.locale;
      Object.assign(this.calendarOptions, this.configService.viewConfig(this.view, this.locale));
    });
  }

  public getCalendarByGroupEntry(arg: EventContentArg): EventCalendarEntry {
    return arg.event.extendedProps['entry'];
  }

  public getStringTitleFromArray(items: (string | undefined | null)[]): string {
    return items.filter(i => i).join(', ');
  }

  private emitChangeDate(start: DateTime, view: CalendarViewEnum = this.currentView): void {
    this.changeDate.next({
      start,
      updateHistory: true,
      view,
      filter: this.filter,
      favorite: this.favorite,
    });
  }

  public loadData(): void {
    if (!this.currentStart) return;
    this.dataService.load(this.currentStart, this.view).pipe(takeUntil(this.onDestroy$)).subscribe(({resources, events}) => {
      this.calendarOptions.resources = resources;
      this.calendarComponent?.getApi().removeAllEvents();
      this.calendarOptions.events = events;
      this.cdr.markForCheck();
    });
  }

  /** Date functions */
  public thisWeek(): void {
    this.emitChangeDate(DateTime.now().setZone(BERLIN_TZ).startOf('week'), CalendarViewEnum.timeGridWeek);
  }

  // go to next week (relative to now)
  public nextWeek(amount = 1): void {
    this.emitChangeDate(DateTime.now().setZone(BERLIN_TZ).plus({weeks: amount}).startOf('week'), CalendarViewEnum.timeGridWeek);
  }

  public onWeekJump(weeks: number): void {
    if (weeks === 0) {
      this.thisWeek();
    } else {
      this.nextWeek(weeks);
    }
  }

  public datePickerChanged($event: Date | undefined) {
    if (!$event) return;

    if (Number.isNaN($event.getTime())) return;

    const received = DateTime.local($event.getFullYear(), $event.getMonth() + 1, $event.getDate(), 0, 0, 0, 0, {zone: BERLIN_TZ})


    if (!this.currentStart) return;

    if (received.toMillis() === this.currentStart.startOf('day').toMillis()) return;

    this.emitChangeDate(received);
  }

  /** Filter functions */
  public toggleFilter() {
    this.showFilters = !this.showFilters;
    this.cdr.markForCheck();
  }

  public filterLoaded(event: TableDisplayConfig) {
    if (!areObjectsIdentical(event, this.tableDisplayConfig)) {
      this.filter = event.filter
      this.favorite = event.favorite;
      this.tableDisplayConfig = {...this.tableDisplayConfig, filter: event.filter, favorite: event.favorite};
    }

    if (this.currentStart) {
      this.emitChangeDate(this.currentStart);
    }
  }

  public filtersChanged(event: TableDisplayConfig) {
    if (!areObjectsIdentical(event.filter, this.tableDisplayConfig.filter)) {
      this.filter = event.filter
      this.tableDisplayConfig = {...this.tableDisplayConfig, filter: event.filter};
    }

    if (this.currentStart) {
      this.emitChangeDate(this.currentStart);
    }
  }

  public resetFilter() {
    this.filterLoaded({
      ...this.tableDisplayConfig,
      filter: {},
      favorite: undefined
    });
  }

  private buildEventResourceInfo(entry: EventCalendarEntry | EventCalendarMonthEntry): InfoboxData {
    const lines: InfoboxLine[] = [];
    const ev = entry.event;
    const er = entry.event_resource;

    if (ev?.project_manager_one) {
      lines.push({value: `PM (${ev.project_manager_one.last_name}, ${(ev.project_manager_one.first_name ?? ' ')[0]})`});
    }
    if (ev?.operations_manager_one) {
      lines.push({value: `OM (${ev.operations_manager_one.last_name}, ${(ev.operations_manager_one.first_name ?? ' ')[0]})`});
    }

    const fmt = (iso?: string | null) =>
      iso ? DateTime.fromISO(iso).setZone(BERLIN_TZ).toFormat('dd.MM.yyyy HH:mm') : '';

    const monthEntry = entry as EventCalendarMonthEntry;
    const setupAt = this.view === 'month' ? monthEntry.setup_phase_start_at : er?.setup_phase_start_at;
    if (setupAt) {
      lines.push({label: this.translate.instant('enums.eventPhaseEnum.setup_phase'), value: fmt(setupAt)});
    }

    if (er?.event_phase_start_at && er?.event_phase_end_at) {
      lines.push({
        label: this.translate.instant('enums.eventPhaseEnum.event_phase'),
        value: `${fmt(er.event_phase_start_at)} - ${fmt(er.event_phase_end_at)}`,
      });
    }

    const teardownAt = this.view === 'month' ? monthEntry.teardown_phase_end_at : er?.teardown_phase_end_at;
    if (teardownAt) {
      lines.push({label: this.translate.instant('enums.eventPhaseEnum.teardown_phase'), value: fmt(teardownAt)});
    }

    lines.push({value: this.translate.instant(`enums.eventResourceStateEnum.${er?.state}`)});

    return {
      title: `${ev?.title ?? ''}, ${ev?.ext_event_id ?? ''}`,
      lines,
    };
  }

  private buildBlockedResourceInfo(entry: EventCalendarEntry): InfoboxData {
    const br = entry.blocked_resource;
    const def = br?.blocked_resource_definition;
    const lines: InfoboxLine[] = [
      {value: this.translate.instant(`enums.blockedResourceCategoryEnum.${def?.category}`)},
    ];
    if (def?.event_id) {
      if (def.event?.title) lines.push({value: def.event.title});
      if (def.event?.ext_event_id) lines.push({value: def.event.ext_event_id});
    }
    return {
      title: def?.title ?? '',
      lines,
    };
  }

  private buildOccurrenceInfo(entry: EventCalendarEntry): InfoboxData {
    const occ = entry.occurrence as Occurrence;
    const fmt = (iso?: string | null) =>
      iso ? DateTime.fromISO(iso).setZone(BERLIN_TZ).toFormat('dd.MM.yyyy HH:mm') : '';
    return {
      title: occ?.internal_title ?? '',
      lines: [
        {value: this.translate.instant(`enums.occurrenceCategoryEnum.${occ?.category}`)},
        {value: `${fmt(entry.start_at)} - ${fmt(entry.end_at)}`},
        ...(occ?.internal_comment ? [{value: occ.internal_comment}] : []),
      ],
    };
  }

  public entryClicked(entry: EventCalendarEntry | EventCalendarMonthEntry): void {
    let data: InfoboxData;
    switch (entry.type) {
      case EventmanagementCalendarEntryTypeEnum.EVENT_EVENT_RESOURCE:
        data = this.buildEventResourceInfo(entry);
        break;
      case EventmanagementCalendarEntryTypeEnum.BLOCKED_RESOURCE:
        data = this.buildBlockedResourceInfo(entry as EventCalendarEntry);
        break;
      case EventmanagementCalendarEntryTypeEnum.OCCURRENCE:
        data = this.buildOccurrenceInfo(entry as EventCalendarEntry);
        break;
      default:
        return;
    }

    const oldMaxOpened = this.toastrService.toastrConfig.maxOpened;
    const oldAutoDismiss = this.toastrService.toastrConfig.autoDismiss;
    this.toastrService.toastrConfig.maxOpened = 1;
    this.toastrService.toastrConfig.autoDismiss = true;

    try {
      this.toastrService.show(undefined, data.title, {
        toastComponent: EventCalendarInfoboxComponent,
        payload: data,
        tapToDismiss: false,
        positionClass: 'toast-top-center',
        timeOut: 0,
        extendedTimeOut: 0,
        easeTime: 0,
      }, 'toast-info');
    } finally {
      this.toastrService.toastrConfig.maxOpened = oldMaxOpened;
      this.toastrService.toastrConfig.autoDismiss = oldAutoDismiss;
    }
  }

  public override ngAfterViewInit() {
    super.ngAfterViewInit();

    this.activatedRoute.url.pipe(takeUntil(this.onDestroy$)).subscribe(url => {
      const segment = url[url.length - 1].path;
      const validViews: ReadonlyArray<'day' | 'week' | 'month'> = ['day', 'week', 'month'];
      this.view = validViews.includes(segment as 'day' | 'week' | 'month') ? (segment as 'day' | 'week' | 'month') : 'month';

      this.cdr.markForCheck();

      const fullCalendarView = `resourceTimeline${this.view.charAt(0).toUpperCase() + this.view.slice(1)}`;
      this.calendarComponent?.getApi().changeView(fullCalendarView);
      Object.assign(this.calendarOptions, this.configService.viewConfig(this.view, this.locale));
      return;
    });
  }

  private buildCustomButtons() {
    const labels = this.configService.buttonLabels(this.locale);
    return (['day', 'week', 'month'] as const).reduce((acc, view) => {
      acc[view] = {
        text: labels[view],
        click: () => this.navigateToView(view),
      };
      return acc;
    }, {} as Record<CalendarView, {text: string; click: () => void}>);
  }

  private navigateToView(view: CalendarView): void {
    const now = DateTime.now().setZone(BERLIN_TZ);
    const isCurrentMonth = this.currentStart?.hasSame(now, 'month');
    const targetStart = view === 'month'
      ? this.currentStart
      : (isCurrentMonth
          ? (view === 'day' ? now.startOf('day') : now.startOf('week'))
          : (view === 'day' ? this.currentStart?.startOf('month') : this.currentStart?.startOf('month').startOf('week')));

    this.router.navigate(['event-calendar', view], {
      queryParams: {start: toBerlinIso(targetStart)},
      replaceUrl: true,
    });
  }

  protected readonly PERMISSION = PERMISSION;
}
